import { useMutation, useQueryClient } from 'react-query';
import {
  MutationKeyConfig,
  postData,
  postDataFastField,
  postDataFastFieldRecall,
  postDataForDux,
  postDataForQuickBook,
  putData,
  QueryKeyConfig,
} from '../api/api-helper';
import { ApiResponse, ApiResponseForDuxLoing, ApiResponseForPost } from '../models/api-response';
import { useSnackbar } from './use-snackbar.hook';
import { LoginResponseForDux } from '../models/login-info';

type InvalidateRoute = (data: any) => QueryKeyConfig;

interface MutationFnParam {
  route?: string;
  data: any;
}

interface ErrorResponse {
  message: string;
  code: string;
  status: number;
}

// const getErros = (response: any): ErrorResponse[] | null => {
//   if (response && response.data && response.data.errors) {
//     const errorResponse: ErrorResponse[] = Object.keys(response.data.errors).map((key: string) => {
//       const errorMessage = response.data.errors[key][0]; // Assuming only the first error message is needed
//       return {
//         message: errorMessage,
//         code: key,
//         status: response.status || 500, // Default to 500 if status is not provided
//       };
//     });
//     return errorResponse;
//   } else if (response && response.message) {
//     const errorResponse: ErrorResponse[] = [
//       {
//         message: response.message,
//         code: response.code || '',
//         status: response.status || 500, // Default to 500 if status is not provided
//       },
//     ];
//     return errorResponse;
//   } else {
//     return null; // No error message found in the response
//   }
// };

const getErros = (response: any): ErrorResponse | null => {
  if (response && response.data && response.data.message) {
    const errorMessage = response.data.message;
    return {
      message: errorMessage,
      code: response?.data?.code,
      status: response.status || 500, // Default to 500 if status is not provided
    };
  } else if (response && response.message) {
    const errorResponse: ErrorResponse = {
      message: response.message,
      code: response.code || '',
      status: response.status || 500, // Default to 500 if status is not provided
    };

    return errorResponse;
  } else {
    return null; // No error message found in the response
  }
};

export const useSaveData = <T>(config: MutationKeyConfig, invalidateRoute?: InvalidateRoute) => {
  const { showSnackbar } = useSnackbar();
  const client = useQueryClient();
  return useMutation({
    mutationKey: config.mutationKey,
    mutationFn: (value: MutationFnParam) =>
      config.action === 'POST'
        ? postData<ApiResponse<T>>(value.route ? value.route : config.route, value.data)
        : putData<ApiResponse<T>>(value.route ? value.route : config.route, value.data),
    onSuccess: ({ data }: any) => {
      if (!config.disableSnackbar) {
        showSnackbar('Changes saved successfully', 'success');
      }
      if (invalidateRoute) {
        client.invalidateQueries(invalidateRoute(data).queryKey);
      }
    },
    onError: (error: any) => {
      const messages = getErros(error.response);

      // if (messages && messages.length > 0) {
      //   const errorCount = messages.length > 1 ? ` | +${messages.length - 1} more` : '';
      //   showSnackbar(`Error: ${messages[0].message} ${errorCount}`, 'error');
      // } else {
      //   showSnackbar(error?.message ?? 'Failed to save data', 'error');
      // }

      if (!config?.disableFailSnackbar) {
        if (messages) {
          showSnackbar(`Error: ${messages.message} `, 'error');
        } else {
          showSnackbar(error?.message ?? 'Failed to save data', 'error');
        }
      }
    },
  });
};

export const useSaveDataForPost = <T>(config: MutationKeyConfig, invalidateRoute?: InvalidateRoute) => {
  const { showSnackbar } = useSnackbar();
  const client = useQueryClient();
  return useMutation({
    mutationKey: config.mutationKey,
    mutationFn: (value: MutationFnParam) =>
      config.action === 'POST'
        ? postData<ApiResponseForPost<T>>(value.route ? value.route : config.route, value.data)
        : putData<ApiResponseForPost<T>>(value.route ? value.route : config.route, value.data),
    onSuccess: ({ data }: any) => {
      if (!config.disableSnackbar) {
        showSnackbar('Changes saved successfully', 'success');
      }
      if (invalidateRoute) {
        client.invalidateQueries(invalidateRoute(data).queryKey);
      }
    },
    onError: (error: any) => {
      const messages = getErros(error.response);

      // if (messages && messages.length > 0) {
      //   const errorCount = messages.length > 1 ? ` | +${messages.length - 1} more` : '';
      //   showSnackbar(`Error: ${messages[0].message} ${errorCount}`, 'error');
      // } else {
      //   showSnackbar(error?.message ?? 'Failed to save data', 'error');
      // }

      if (messages) {
        showSnackbar(`Error: ${messages.message} `, 'error');
      } else {
        showSnackbar(error?.message ?? 'Failed to save data', 'error');
      }
    },
  });
};

// only for fastfield api call
export const useSaveDataForFastField = <T>(config: MutationKeyConfig, invalidateRoute?: InvalidateRoute) => {
  const { showSnackbar } = useSnackbar();
  const client = useQueryClient();
  return useMutation({
    mutationKey: config.mutationKey,
    mutationFn: (value: MutationFnParam) =>
      config.action === 'POST'
        ? postDataFastField<ApiResponse<T>>(value.route ? value.route : config.route, value.data)
        : putData<ApiResponse<T>>(value.route ? value.route : config.route, value.data),
    onSuccess: ({ data }: any) => {
      if (!config.disableSnackbar) {
        showSnackbar('Changes saved successfully', 'success');
      }
      if (invalidateRoute) {
        client.invalidateQueries(invalidateRoute(data).queryKey);
      }
    },
    onError: (error: any) => {
      const messages = getErros(error.response);

      // if (messages && messages.length > 0) {
      //   const errorCount = messages.length > 1 ? ` | +${messages.length - 1} more` : '';
      //   showSnackbar(`Error: ${messages[0].message} ${errorCount}`, 'error');
      // } else {
      //   showSnackbar(error?.message ?? 'Failed to save data', 'error');
      // }

      if (messages) {
        showSnackbar(`Error: ${messages.message} `, 'error');
      } else {
        showSnackbar(error?.message ?? 'Failed to save data', 'error');
      }
    },
  });
};

export const useSaveDataForFastFieldRecall = <T>(config: MutationKeyConfig, invalidateRoute?: InvalidateRoute) => {
  const { showSnackbar } = useSnackbar();
  const client = useQueryClient();
  return useMutation({
    mutationKey: config.mutationKey,
    mutationFn: (value: MutationFnParam) =>
      config.action === 'POST'
        ? postDataFastFieldRecall<ApiResponse<T>>(value.route ? value.route : config.route, value.data)
        : putData<ApiResponse<T>>(value.route ? value.route : config.route, value.data),
    onSuccess: ({ data }: any) => {
      if (!config.disableSnackbar) {
        showSnackbar('Changes saved successfully', 'success');
      }
      if (invalidateRoute) {
        client.invalidateQueries(invalidateRoute(data).queryKey);
      }
    },
    onError: (error: any) => {
      const messages = getErros(error.response);

      // if (messages && messages.length > 0) {
      //   const errorCount = messages.length > 1 ? ` | +${messages.length - 1} more` : '';
      //   showSnackbar(`Error: ${messages[0].message} ${errorCount}`, 'error');
      // } else {
      //   showSnackbar(error?.message ?? 'Failed to save data', 'error');
      // }

      if (messages) {
        showSnackbar(`Error: ${messages.message} `, 'error');
      } else {
        showSnackbar(error?.message ?? 'Failed to save data', 'error');
      }
    },
  });
};

// dux

export const useSaveDataForPostInDux = <T>(config: MutationKeyConfig, invalidateRoute?: InvalidateRoute) => {
  const { showSnackbar } = useSnackbar();
  const client = useQueryClient();
  return useMutation({
    mutationKey: config.mutationKey,
    mutationFn: (value: MutationFnParam) =>
      config.action === 'POST'
        ? postDataForDux<ApiResponseForDuxLoing<T>>(value.route ? value.route : config.route, value.data)
        : putData<ApiResponseForDuxLoing<T>>(value.route ? value.route : config.route, value.data),
    onSuccess: ({ data }: any) => {
      if (!config.disableSnackbar) {
        showSnackbar('Changes saved successfully', 'success');
      }
      if (invalidateRoute) {
        client.invalidateQueries(invalidateRoute(data).queryKey);
      }
    },
    onError: (error: any) => {
      const messages = getErros(error.response);

      // if (messages && messages.length > 0) {
      //   const errorCount = messages.length > 1 ? ` | +${messages.length - 1} more` : '';
      //   showSnackbar(`Error: ${messages[0].message} ${errorCount}`, 'error');
      // } else {
      //   showSnackbar(error?.message ?? 'Failed to save data', 'error');
      // }

      if (messages) {
        // showSnackbar(`Error: ${messages.message} `, 'error');
      } else {
        // showSnackbar(error?.message ?? 'Failed to save data', 'error');
      }
    },
  });
};

export const useSaveDataForQuickBook = <T>(config: MutationKeyConfig, invalidateRoute?: InvalidateRoute) => {
  const { showSnackbar } = useSnackbar();
  const client = useQueryClient();
  return useMutation({
    mutationKey: config.mutationKey,
    mutationFn: (value: MutationFnParam) =>
      config.action === 'POST'
        ? postDataForQuickBook<ApiResponse<T>>(value.route ? value.route : config.route, value.data)
        : putData<ApiResponse<T>>(value.route ? value.route : config.route, value.data),
    onSuccess: ({ data }: any) => {
      if (!config.disableSnackbar) {
        showSnackbar('Changes saved successfully', 'success');
      }
      if (invalidateRoute) {
        client.invalidateQueries(invalidateRoute(data).queryKey);
      }
    },
    onError: (error: any) => {
      const messages = getErros(error.response);

      // if (messages && messages.length > 0) {
      //   const errorCount = messages.length > 1 ? ` | +${messages.length - 1} more` : '';
      //   showSnackbar(`Error: ${messages[0].message} ${errorCount}`, 'error');
      // } else {
      //   showSnackbar(error?.message ?? 'Failed to save data', 'error');
      // }

      if (messages) {
        showSnackbar(`Error: ${messages.message} `, 'error');
      } else {
        showSnackbar(error?.message ?? 'Failed to save data', 'error');
      }
    },
  });
};
